/**
 * The builder's draft, autosaved in this browser so a reload or a closed tab
 * doesn't lose a half-built plan: one draft per signed-in user, kept 30 days.
 * (The dashboard uses BrowserRouter, so there is no route blocker to warn on
 * leaving — this is the safety net.) Storage can be unavailable or full
 * (private windows, blocked site data), so every read and write is guarded
 * and the builder works without it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MEDIA_PLAN_UNIT_ORDER, type MediaPlanUnitKey } from './types';
import { newDraft, newSegmentDraft, type PlanDraft, type RateEdit, type SegmentDraft } from './planDraft';

export const DRAFT_STORAGE_PREFIX = 'cg.mediaPlans.draft.v1.';
export const DRAFT_KEEP_MS = 30 * 24 * 60 * 60 * 1000;
export const DRAFT_SAVE_DELAY_MS = 400;

export interface StoredDraft {
  savedAt: number;
  draft: PlanDraft;
  /** Opened as a new version of this plan. */
  revisesPlanId?: string;
  revisesLabel?: string;
  /** Opened from this Culture Genius chat draft. */
  chatDraftId?: string;
}

export type DraftToStore = Omit<StoredDraft, 'savedAt'>;

const storageKey = (uid: string): string => `${DRAFT_STORAGE_PREFIX}${uid}`;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

function unitStrings(v: unknown): Partial<Record<MediaPlanUnitKey, string>> {
  const out: Partial<Record<MediaPlanUnitKey, string>> = {};
  if (!isRecord(v)) return out;
  for (const key of MEDIA_PLAN_UNIT_ORDER) if (typeof v[key] === 'string') out[key] = v[key] as string;
  return out;
}

function rateEdits(v: unknown): Partial<Record<MediaPlanUnitKey, RateEdit>> {
  const out: Partial<Record<MediaPlanUnitKey, RateEdit>> = {};
  if (!isRecord(v)) return out;
  for (const key of MEDIA_PLAN_UNIT_ORDER) {
    const edit = v[key];
    if (isRecord(edit) && typeof edit.value === 'string' && (edit.origin === 'typed' || edit.origin === 'adopted' || edit.origin === 'prior')) {
      out[key] = { value: edit.value, origin: edit.origin };
    }
  }
  return out;
}

function segmentFrom(v: unknown): SegmentDraft | null {
  if (!isRecord(v) || typeof v.id !== 'string' || typeof v.label !== 'string') return null;
  const geoScope = v.geoScope === 'dma' || v.geoScope === 'state' || v.geoScope === 'zip' ? v.geoScope : 'national';
  return {
    ...newSegmentDraft(v.label),
    id: v.id,
    description: str(v.description),
    budgetPct: str(v.budgetPct),
    demo: strings(v.demo),
    cultureStacks: strings(v.cultureStacks),
    geoScope,
    geoValuesText: str(v.geoValuesText),
    dayparts: strings(v.dayparts),
    competitiveConquesting: strings(v.competitiveConquesting),
    useOwnMix: v.useOwnMix === true,
    unitMix: unitStrings(v.unitMix),
    rates: rateEdits(v.rates),
  };
}

/** A stored draft back as a PlanDraft, filling anything missing with defaults; null when it isn't one. */
export function draftFrom(v: unknown): PlanDraft | null {
  if (!isRecord(v) || !Array.isArray(v.segments)) return null;
  const segments = v.segments.map(segmentFrom);
  if (!segments.length || segments.some((s) => s === null)) return null;
  const base = newDraft();
  const reasons: Record<string, string> = {};
  if (isRecord(v.reasons)) for (const [k, text] of Object.entries(v.reasons)) if (typeof text === 'string') reasons[k] = text;
  const prior = isRecord(v.prior) && typeof v.prior.version === 'number' && isRecord(v.prior.rates) ? (v.prior as PlanDraft['prior']) : undefined;
  return {
    ...base,
    advertiserKey: str(v.advertiserKey),
    advertiserName: str(v.advertiserName),
    agencyKey: str(v.agencyKey) || base.agencyKey,
    agencyOther: str(v.agencyOther),
    objective: str(v.objective),
    budgetUsd: str(v.budgetUsd),
    tiersUsd: strings(v.tiersUsd),
    flightStart: str(v.flightStart),
    flightEnd: str(v.flightEnd),
    notes: str(v.notes),
    useCardMix: v.useCardMix !== false,
    unitMix: isRecord(v.unitMix) ? unitStrings(v.unitMix) : base.unitMix,
    segments: segments as SegmentDraft[],
    upchargesMode: v.upchargesMode === 'card' ? 'card' : 'none',
    includeAddedValue: v.includeAddedValue !== false,
    addedValuePct: str(v.addedValuePct),
    roundImpressionsTo: v.roundImpressionsTo === 1000 ? 1000 : 1,
    reasons,
    prior,
  };
}

export function clearStoredDraft(uid: string | null): void {
  if (!uid) return;
  try {
    storage()?.removeItem(storageKey(uid));
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** The signed-in user's saved draft, or null (none, unreadable, or older than 30 days — those are removed). */
export function loadStoredDraft(uid: string | null, now: number = Date.now()): StoredDraft | null {
  if (!uid) return null;
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(storageKey(uid)) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearStoredDraft(uid);
    return null;
  }
  const draft = isRecord(parsed) ? draftFrom(parsed.draft) : null;
  const savedAt = isRecord(parsed) && typeof parsed.savedAt === 'number' ? parsed.savedAt : 0;
  if (!isRecord(parsed) || !draft || now - savedAt > DRAFT_KEEP_MS) {
    clearStoredDraft(uid);
    return null;
  }
  return {
    savedAt,
    draft,
    revisesPlanId: str(parsed.revisesPlanId) || undefined,
    revisesLabel: str(parsed.revisesLabel) || undefined,
    chatDraftId: str(parsed.chatDraftId) || undefined,
  };
}

/** Write the draft now; returns when it was saved, or null when storage refused it. */
export function saveStoredDraft(uid: string, value: DraftToStore, now: number = Date.now()): number | null {
  try {
    const store = storage();
    if (!store) return null;
    store.setItem(storageKey(uid), JSON.stringify({ ...value, savedAt: now }));
    return now;
  } catch {
    return null;
  }
}

export interface DraftAutosave {
  /** When the draft was last written (null until the first save). */
  savedAt: number | null;
  /** The last write was refused (storage blocked or full). */
  failed: boolean;
  /** Remove the saved draft and stop saving for this builder (after create, or on Discard). */
  discard: () => void;
}

/**
 * Save `value` 400 ms after it last changed, and once more on unmount or
 * page hide if a save is pending. `value` null saves nothing (an untouched
 * builder, or one waiting to be told it may replace another draft).
 */
export function useDraftAutosave(
  uid: string | null,
  value: DraftToStore | null,
  opts: { delayMs?: number; initialSavedAt?: number | null } = {},
): DraftAutosave {
  const delayMs = opts.delayMs ?? DRAFT_SAVE_DELAY_MS;
  const [savedAt, setSavedAt] = useState<number | null>(opts.initialSavedAt ?? null);
  const [failed, setFailed] = useState(false);
  const pending = useRef<DraftToStore | null>(null);
  const stopped = useRef(false);
  const mounted = useRef(true);
  const serialized = value ? JSON.stringify(value) : null;
  const valueRef = useRef(value);
  valueRef.current = value;

  const flush = useCallback(() => {
    const next = pending.current;
    pending.current = null;
    if (!uid || !next || stopped.current) return;
    const at = saveStoredDraft(uid, next);
    if (!mounted.current) return;
    setFailed(at === null);
    if (at !== null) setSavedAt(at);
  }, [uid]);

  useEffect(() => {
    if (serialized === null || stopped.current) return;
    pending.current = valueRef.current;
    const timer = setTimeout(flush, delayMs);
    return () => clearTimeout(timer);
  }, [serialized, delayMs, flush]);

  useEffect(() => {
    mounted.current = true;
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      mounted.current = false;
      flush();
    };
  }, [flush]);

  const discard = useCallback(() => {
    stopped.current = true;
    pending.current = null;
    clearStoredDraft(uid);
    setSavedAt(null);
  }, [uid]);

  return { savedAt, failed, discard };
}

/** "State Farm · $250K" — how a saved draft is named on the list's resume banner. */
export function storedDraftTitle(stored: StoredDraft, fmtBudget: (usd: number) => string): string {
  const name = stored.draft.advertiserName.trim() || 'Untitled plan';
  const budget = Number(stored.draft.budgetUsd.replace(/[$,\s]/g, ''));
  return Number.isFinite(budget) && budget > 0 ? `${name} · ${fmtBudget(budget)}` : name;
}
